import { useEffect, useMemo, useState } from 'react'
import apiClient from '../../api/apiClient'
import { customerRegistryApi } from '../../api/customerRegistry'
import { useCustomerStore } from '../../stores/customerStore'
import { formatCurrency } from '../../utils/utils'
import { Card } from '../ui/Card'
import { CustomerFormModal } from './CustomerFormModal'
import type { CustomerYaml } from '../../types/customer'

interface UrgentCustomer {
  customerName: string
  type: 'renewal' | 'expiry'
  date: string
  daysLeft: number
}

interface CustomerSummary {
  customerName: string
  tenancyName: string
  profile: string
  contractAmount: number
  balanceExhaustionDate: string
  expiryDate: string
  currentBalance: number
  monthlyUsage: number
  recentDailyCosts: number[]  // 최근 7일간 비용 (오래된 날짜부터)
  recentDates: any[]  // 최근 7일간 날짜 (오래된 날짜부터)
}

interface CostChangeCustomer {
  customerName: string
  todayCost: number
  avgCost: number
  changePercent: number
}

export function AllCustomersGrid() {
  const { customers, setSelectedCustomer, loadCustomers } = useCustomerStore()
  const [customersData, setCustomersData] = useState<CustomerSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>('')
  const [isInsightOpen, setIsInsightOpen] = useState(true)

  // --- 고객 등록/수정/삭제 상태 ---
  const [registryEntries, setRegistryEntries] = useState<CustomerYaml[]>([])
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
  const [modalInitial, setModalInitial] = useState<CustomerYaml | null>(null)
  const [deletingProfile, setDeletingProfile] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<CustomerYaml | null>(null)

  const userRole = useMemo(() => {
    try {
      const raw = localStorage.getItem('user_info')
      return raw ? (JSON.parse(raw).role as string) : ''
    } catch {
      return ''
    }
  }, [])
  const canManage = userRole === 'ADMIN' || userRole === 'ENGINEER'

  const profileByCustomerName = useMemo(() => {
    const m = new Map<string, CustomerYaml>()
    for (const c of registryEntries) m.set(c.name, c)
    return m
  }, [registryEntries])

  const loadRegistry = async () => {
    try {
      const list = await customerRegistryApi.list()
      setRegistryEntries(list)
    } catch (err) {
      console.error('Failed to load customer registry:', err)
    }
  }

  useEffect(() => {
    loadAllCustomersSummary()
  }, [customers])

  useEffect(() => {
    loadRegistry()
  }, [])

  const handleOpenCreate = () => {
    setModalMode('create')
    setModalInitial(null)
    setModalOpen(true)
  }

  const handleOpenEdit = (e: React.MouseEvent, customerName: string) => {
    e.stopPropagation()
    const entry = profileByCustomerName.get(customerName)
    if (!entry) {
      alert('레지스트리에서 해당 고객 정보를 찾지 못했습니다. 새로고침 후 다시 시도하세요.')
      return
    }
    setModalMode('edit')
    setModalInitial(entry)
    setModalOpen(true)
  }

  const handleOpenDelete = (e: React.MouseEvent, customerName: string) => {
    e.stopPropagation()
    const entry = profileByCustomerName.get(customerName)
    if (!entry) return
    setConfirmDelete(entry)
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    setDeletingProfile(confirmDelete.profile)
    try {
      await customerRegistryApi.remove(confirmDelete.profile)
      setConfirmDelete(null)
      await loadRegistry()
      await loadCustomers()
    } catch (err: any) {
      alert(err?.response?.data?.message || '삭제에 실패했습니다.')
    } finally {
      setDeletingProfile(null)
    }
  }

  const handleSaved = async () => {
    setModalOpen(false)
    await loadRegistry()
    await loadCustomers()
  }

  const loadAllCustomersSummary = async () => {
    if (customers.length === 0) return

    setLoading(true)
    setError('')

    try {
      // 1. 먼저 /api/customers에서 기본 정보 가져오기
      const customersResponse = await apiClient.get<any[]>('/api/customers')
      const customersFullData = customersResponse.data

      // 2. 각 고객의 사용량 데이터를 병렬로 로드
      const summaryPromises = customers.map(async (customerName) => {
        try {
          // 기본 정보 찾기
          const customerInfo = customersFullData.find((c: any) => c.customerName === customerName)
          if (!customerInfo) {
            console.error(`Customer info not found for ${customerName}`)
            return null
          }

          // 현재 월 데이터 가져오기
          const now = new Date()
          const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`

          const response = await apiClient.get<any>(
            `/api/customers/${encodeURIComponent(customerName)}/usage/monthly?yearMonth=${yearMonth}`
          )
          const data = response.data

          // 현재 잔액 (말일 기준)
          const currentBalance = data.dailyData?.[0]?.remainingBalance || 0

          // 월 사용량 계산 (최근 30일)
          const recentData = data.dailyData?.slice(0, 30) || []
          const monthlyUsage = recentData.reduce((sum: number, day: any) => sum + (day.totalUsage || 0), 0)

          // 최근 7일간 비용 및 날짜 (차트용 - 오래된 순으로 정렬)
          const recent7Days = data.dailyData?.slice(0, 7) || []
          const recentDailyCosts = recent7Days.map((day: any) => day.totalUsage || 0).reverse() // 오래된 날짜부터
          const recentDates = recent7Days.map((day: any) => day.date).reverse() // 오래된 날짜부터

          // 날짜 배열을 문자열로 변환
          const formatDateArray = (dateValue: any): string => {
            if (!dateValue || dateValue === 'N/A') return 'N/A'
            if (Array.isArray(dateValue)) {
              return JSON.stringify(dateValue) // [2026, 4, 3] → "[2026,4,3]"
            }
            return String(dateValue)
          }

          return {
            customerName: customerName,
            tenancyName: customerInfo.tenancyName || customerName,
            profile: customerInfo.profile || '',
            contractAmount: customerInfo.contractAmount || 0,
            balanceExhaustionDate: formatDateArray(customerInfo.balanceExhaustionDate),
            expiryDate: formatDateArray(customerInfo.expiryDate),
            currentBalance,
            monthlyUsage,
            recentDailyCosts,
            recentDates  // 실제 날짜 추가
          }
        } catch (err) {
          console.error(`Failed to load data for ${customerName}:`, err)
          return null
        }
      })

      const results = await Promise.all(summaryPromises)
      const validResults = results.filter((r): r is CustomerSummary => r !== null && r !== undefined)

      // 날짜 파싱 함수: null/N/A는 Infinity (맨 아래)로 처리
      const parseDate = (dateStr: string) => {
        if (!dateStr || dateStr === 'N/A') return new Date(Infinity)
        // JSON 배열 형식인 경우
        if (dateStr.startsWith('[')) {
          const parts = JSON.parse(dateStr)
          return new Date(parts[0], parts[1] - 1, parts[2])
        }
        return new Date(dateStr)
      }

      // 갱신예상일과 계약만료일 중 더 빠른 날짜 기준 정렬
      // 계약 정보가 없는 고객(null/N/A)은 맨 아래로 정렬
      const sorted = validResults.sort((a, b) => {
        const aRenewalDate = parseDate(a.balanceExhaustionDate)
        const aExpiryDate = parseDate(a.expiryDate)
        const bRenewalDate = parseDate(b.balanceExhaustionDate)
        const bExpiryDate = parseDate(b.expiryDate)

        // 두 날짜 중 더 빠른 날짜 선택
        const aEarliestDate = aRenewalDate < aExpiryDate ? aRenewalDate : aExpiryDate
        const bEarliestDate = bRenewalDate < bExpiryDate ? bRenewalDate : bExpiryDate

        // Infinity끼리 비교 시 0 반환 (순서 유지)
        if (!isFinite(aEarliestDate.getTime()) && !isFinite(bEarliestDate.getTime())) {
          return 0
        }

        return aEarliestDate.getTime() - bEarliestDate.getTime()
      })

      setCustomersData(sorted)
    } catch (err) {
      setError('고객 데이터를 불러오는데 실패했습니다.')
      console.error('Failed to load all customers summary:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr: string) => {
    if (dateStr === 'N/A' || !dateStr || dateStr === '') return '계산 불가'

    try {
      // JSON 배열 형식 처리
      if (dateStr.startsWith('[')) {
        const parts = JSON.parse(dateStr)
        return `${parts[0]}-${String(parts[1]).padStart(2, '0')}-${String(parts[2]).padStart(2, '0')}`
      }
      return dateStr
    } catch (e) {
      return dateStr
    }
  }

  const getDaysUntilExpiration = (dateStr: string): number => {
    if (dateStr === 'N/A') return 9999

    try {
      let expirationDate: Date

      if (dateStr.startsWith('[')) {
        const parts = JSON.parse(dateStr)
        expirationDate = new Date(parts[0], parts[1] - 1, parts[2])
      } else {
        expirationDate = new Date(dateStr)
      }

      const today = new Date()
      const diffTime = expirationDate.getTime() - today.getTime()
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      return diffDays
    } catch (e) {
      return 9999
    }
  }

  // 재계약 임박 고객 목록 생성 (날짜가 지난 것 제외)
  const getUrgentCustomers = (): UrgentCustomer[] => {
    const urgent: UrgentCustomer[] = []

    customersData.forEach((customer) => {
      const renewalDays = getDaysUntilExpiration(customer.balanceExhaustionDate)
      const expiryDays = getDaysUntilExpiration(customer.expiryDate)

      // 갱신과 만료 중 더 빨리 도래하는 것 선택 (날짜가 지난 것 제외)
      if (renewalDays <= 90 && renewalDays > 0 && renewalDays < expiryDays) {
        urgent.push({
          customerName: customer.customerName,
          type: 'renewal',
          date: customer.balanceExhaustionDate,
          daysLeft: renewalDays
        })
      } else if (expiryDays <= 90 && expiryDays > 0) {
        urgent.push({
          customerName: customer.customerName,
          type: 'expiry',
          date: customer.expiryDate,
          daysLeft: expiryDays
        })
      }
    })

    // 남은 일수 기준 오름차순 정렬
    return urgent.sort((a, b) => a.daysLeft - b.daysLeft)
  }

  // 비용 변동 고객 목록 생성
  const getCostChangeCustomers = (): CostChangeCustomer[] => {
    const costChanges: CostChangeCustomer[] = []

    customersData.forEach((customer) => {
      if (customer.recentDailyCosts && customer.recentDailyCosts.length >= 4) {
        // 배열은 오래된 날짜부터 정렬: [7일전, 6일전, ..., 최신]
        const costs = customer.recentDailyCosts
        const todayCost = costs[costs.length - 1]  // 최신 데이터
        const last3DaysAvg = (costs[costs.length - 2] + costs[costs.length - 3] + costs[costs.length - 4]) / 3

        if (last3DaysAvg > 0) {
          const changePercent = ((todayCost - last3DaysAvg) / last3DaysAvg) * 100

          // 5% 이상 변동이 있는 경우만 포함
          if (Math.abs(changePercent) >= 5) {
            costChanges.push({
              customerName: customer.customerName,
              todayCost,
              avgCost: last3DaysAvg,
              changePercent
            })
          }
        }
      }
    })

    // 변동률 절대값 기준 내림차순 정렬
    return costChanges.sort((a, b) => Math.abs(b.changePercent) - Math.abs(a.changePercent))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500">전체 고객사 데이터 로딩 중...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error}</div>
      </div>
    )
  }

  const urgentCustomers = getUrgentCustomers()
  const costChangeCustomers = getCostChangeCustomers()

  return (
    <div className="space-y-6">
      {/* 비즈니스 인사이트 섹션 */}
      {(urgentCustomers.length > 0 || costChangeCustomers.length > 0) && (
        <Card className="bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-800 border-2 border-gray-300 dark:border-gray-700">
          <div className="p-4">
            {/* 제목 (드랍다운 버튼) */}
            <button
              onClick={() => setIsInsightOpen(!isInsightOpen)}
              className="w-full flex items-center justify-between"
            >
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-gray-600 dark:bg-gray-700 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div className="text-left">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">비즈니스 인사이트</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">주요 이벤트 및 비용 변동 현황</p>
                </div>
              </div>
              <svg
                className={`w-6 h-6 text-gray-700 dark:text-gray-300 transition-transform ${isInsightOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* 인사이트 그리드 */}
            {isInsightOpen && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              {/* 재계약 임박 고객 */}
              {urgentCustomers.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-red-800 dark:text-red-400 mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    재계약 임박 고객 ({urgentCustomers.length})
                  </h3>
                  <div className="space-y-2">
                    {urgentCustomers.map((urgent) => {
                      const borderColor = urgent.daysLeft <= 30
                        ? 'border-red-500 dark:border-red-800'
                        : 'border-yellow-500 dark:border-yellow-800'
                      const badgeColor = urgent.daysLeft <= 30
                        ? 'bg-red-500 dark:bg-red-900/50 text-white dark:text-red-300'
                        : 'bg-yellow-500 dark:bg-yellow-900/50 text-white dark:text-yellow-300'
                      const textColor = urgent.daysLeft <= 30
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-yellow-600 dark:text-yellow-400'

                      return (
                        <div
                          key={urgent.customerName}
                          onClick={() => setSelectedCustomer(urgent.customerName)}
                          className={`p-3 bg-white dark:bg-gray-900/50 rounded-lg border-l-4 ${borderColor} hover:shadow-md dark:hover:bg-gray-900/70 transition-all cursor-pointer`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{urgent.customerName}</p>
                              <p className="text-xs text-gray-600 dark:text-gray-400">
                                {urgent.type === 'renewal' ? '갱신' : '계약 만료일'}이{' '}
                                <span className={`font-bold ${textColor}`}>{urgent.daysLeft}일</span> 남았습니다.
                              </p>
                            </div>
                            <div className={`px-2 py-1 rounded-full text-xs font-bold ${badgeColor}`}>
                              D-{urgent.daysLeft}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* 비용변동 고객 */}
              {costChangeCustomers.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-orange-800 dark:text-orange-400 mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    비용변동 고객 ({costChangeCustomers.length})
                  </h3>
                  <div className="space-y-2">
                    {costChangeCustomers.map((costChange) => {
                      const isIncrease = costChange.changePercent > 0
                      const borderColor = isIncrease
                        ? 'border-red-500 dark:border-red-800'
                        : 'border-blue-500 dark:border-blue-800'
                      const badgeColor = isIncrease
                        ? 'bg-red-500 dark:bg-red-900/50 text-white dark:text-red-300'
                        : 'bg-blue-500 dark:bg-blue-900/50 text-white dark:text-blue-300'
                      const arrowIcon = isIncrease ? '↑' : '↓'

                      return (
                        <div
                          key={costChange.customerName}
                          onClick={() => setSelectedCustomer(costChange.customerName)}
                          className={`p-3 bg-white dark:bg-gray-900/50 rounded-lg border-l-4 ${borderColor} hover:shadow-md dark:hover:bg-gray-900/70 transition-all cursor-pointer`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{costChange.customerName}</p>
                              <p className="text-xs text-gray-600 dark:text-gray-400">
                                직전 3일 평균 대비{' '}
                                <span className={`font-bold ${isIncrease ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}`}>
                                  {Math.abs(costChange.changePercent).toFixed(1)}% {isIncrease ? '증가' : '감소'}
                                </span>
                              </p>
                            </div>
                            <div className={`px-2 py-1 rounded-full text-xs font-bold ${badgeColor}`}>
                              {arrowIcon} {Math.abs(costChange.changePercent).toFixed(1)}%
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* 전체 고객사 헤더 */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">전체 고객사</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            계약 갱신 예상일이 빠른 순으로 정렬되었습니다 ({customersData.length}개 고객사)
          </p>
        </div>
        {canManage && (
          <button
            onClick={handleOpenCreate}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-gradient-to-r from-primary-700 to-primary-600 dark:from-primary-800 dark:to-primary-700 text-white text-sm font-semibold shadow-md hover:from-primary-600 hover:to-primary-500 transition-all"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
            </svg>
            고객 등록
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {customersData.map((customer) => {
          const renewalDays = getDaysUntilExpiration(customer.balanceExhaustionDate)
          const expiryDays = getDaysUntilExpiration(customer.expiryDate)

          // 갱신예상일과 계약만료일 중 더 빠른 날짜로 긴급도 판단
          const daysLeft = Math.min(renewalDays, expiryDays)
          const isUrgent = daysLeft <= 30
          const isWarning = daysLeft > 30 && daysLeft <= 90

          // 최근 7일 데이터 (recentDailyCosts는 최근 4일만 있으므로 확장 필요)
          const last7Days = customer.recentDailyCosts?.slice(0, 7) || []

          // 비용 변동 색상 결정 (최근 데이터가 있을 경우)
          // 배열은 오래된 날짜부터 정렬되어 있음: [7일전, 6일전, ..., 최신]
          let trendColor = 'rgb(34, 197, 94)' // green-500 (안정)
          if (last7Days.length >= 4) {
            const todayCost = last7Days[last7Days.length - 1]  // 최신 데이터 (마지막 요소)
            const last3DaysAvg = (last7Days[last7Days.length - 2] + last7Days[last7Days.length - 3] + last7Days[last7Days.length - 4]) / 3
            if (last3DaysAvg > 0) {
              const changePercent = ((todayCost - last3DaysAvg) / last3DaysAvg) * 100
              if (changePercent >= 5) {
                trendColor = 'rgb(239, 68, 68)' // red-500 (증가)
              } else if (changePercent <= -5) {
                trendColor = 'rgb(59, 130, 246)' // blue-500 (감소)
              }
            }
          }

          // SVG 미니 차트용 좌표 계산
          const chartWidth = 100
          const chartHeight = 30
          const maxValue = Math.max(...last7Days, 1)
          const points = last7Days.map((value, index) => {
            const x = (chartWidth / (last7Days.length - 1)) * index
            const y = chartHeight - (value / maxValue) * chartHeight
            return `${x},${y}`
          }).join(' ')

          return (
            <div
              key={customer.customerName}
              onClick={() => setSelectedCustomer(customer.customerName)}
              className="cursor-pointer group relative"
            >
              {canManage && profileByCustomerName.has(customer.customerName) && (
                <div className="absolute top-3 right-3 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={(e) => handleOpenEdit(e, customer.customerName)}
                    className="w-8 h-8 rounded-full bg-white/90 dark:bg-gray-900/90 shadow-md border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-primary-50 dark:hover:bg-primary-900/40 hover:text-primary-700 dark:hover:text-primary-400 flex items-center justify-center transition"
                    title="수정"
                    aria-label={`${customer.customerName} 수정`}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 012.828 0l.172.172a2 2 0 010 2.828L12 16H9v-3z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 20h16" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleOpenDelete(e, customer.customerName)}
                    className="w-8 h-8 rounded-full bg-white/90 dark:bg-gray-900/90 shadow-md border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-red-50 dark:hover:bg-red-900/40 hover:text-red-700 dark:hover:text-red-400 flex items-center justify-center transition"
                    title="삭제"
                    aria-label={`${customer.customerName} 삭제`}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M10 7V5a2 2 0 012-2h0a2 2 0 012 2v2" />
                    </svg>
                  </button>
                </div>
              )}
              <Card className="hover:shadow-xl transition-all duration-200">
                <div className="p-4">
                  {/* 고객명 */}
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">{customer.customerName}</h3>
                    {isUrgent && (
                      <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 text-xs font-semibold rounded-full">
                        주의
                      </span>
                    )}
                    {isWarning && (
                      <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 text-xs font-semibold rounded-full">
                        확인필요
                      </span>
                    )}
                  </div>

                  {/* 테넌시명 */}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{customer.tenancyName}</p>

                  {/* 날짜 정보 & 그래프 */}
                  <div className="flex gap-3 mb-3">
                    {/* 잔액소진예상일 & 계약만료일 */}
                    <div className="flex-1 space-y-2">
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">갱신 예상일</p>
                        <p className="text-sm font-bold dark:text-gray-200">
                          <span
                            className={`px-1 ${
                              renewalDays <= 30
                                ? 'bg-red-200 dark:bg-red-900/30'
                                : renewalDays <= 90
                                  ? 'bg-yellow-200 dark:bg-yellow-900/30'
                                  : 'bg-green-200 dark:bg-green-900/30'
                            }`}
                          >
                            {formatDate(customer.balanceExhaustionDate)}
                          </span>
                          {renewalDays < 9999 && (
                            <span className="text-xs text-gray-600 dark:text-gray-400 ml-2">
                              {renewalDays > 0 ? `D-${renewalDays}` : `D+${Math.abs(renewalDays)}`}
                            </span>
                          )}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">계약 만료일</p>
                        <p className="text-sm font-bold dark:text-gray-200">
                          <span
                            className={`px-1 ${
                              expiryDays <= 30
                                ? 'bg-red-200 dark:bg-red-900/30'
                                : expiryDays <= 90
                                  ? 'bg-yellow-200 dark:bg-yellow-900/30'
                                  : 'bg-green-200 dark:bg-green-900/30'
                            }`}
                          >
                            {formatDate(customer.expiryDate)}
                          </span>
                          {expiryDays < 9999 && (
                            <span className="text-xs text-gray-600 dark:text-gray-400 ml-2">
                              {expiryDays > 0 ? `D-${expiryDays}` : `D+${Math.abs(expiryDays)}`}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* 최근 7일 미니 차트 */}
                    {last7Days.length > 0 && (() => {
                      // 실제 데이터 날짜 사용 (recentDates가 있으면 사용, 없으면 인덱스 기반)
                      const dateLabels = customer.recentDates
                        ? customer.recentDates.map((dateStr: string) => {
                            // "2025-11-14" → "11/14"
                            const parts = dateStr.split('-')
                            return parts.length === 3 ? `${parts[1]}/${parts[2]}` : dateStr
                          })
                        : last7Days.map((_, i) => `Day ${i + 1}`)

                      return (
                        <div className="flex-1 p-3 bg-gray-50 dark:bg-gray-900/50 rounded flex flex-col justify-center items-center">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">최근 7일</p>
                          <div className="w-[80%] h-[80%] flex flex-col items-center">
                            {/* 금액 레이블 (그래프 위 오른쪽에만) */}
                            <div className="w-full flex justify-end text-[8px] text-gray-500 dark:text-gray-400 mb-1 px-1">
                              <span>{(last7Days[last7Days.length - 1] / 10000).toFixed(1)}만원</span>
                            </div>

                            {/* SVG 그래프 */}
                            <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full" style={{height: '50px'}} preserveAspectRatio="xMidYMid meet">
                              <polyline
                                fill="none"
                                stroke={trendColor}
                                strokeWidth="2"
                                points={points}
                                vectorEffect="non-scaling-stroke"
                              />
                              {last7Days.map((value, index) => {
                                const x = (chartWidth / (last7Days.length - 1)) * index
                                const y = chartHeight - (value / maxValue) * chartHeight
                                return (
                                  <circle
                                    key={index}
                                    cx={x}
                                    cy={y}
                                    r="1.5"
                                    fill={trendColor}
                                    vectorEffect="non-scaling-stroke"
                                  />
                                )
                              })}
                            </svg>

                            {/* 날짜 레이블 (그래프 아래) */}
                            <div className="w-full flex justify-between text-[7px] text-gray-400 mt-1 px-1">
                              {dateLabels.map((label, index) => (
                                <span key={index}>{label}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )
                    })()}
                  </div>

                  {/* 재무 정보 */}
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">계약금액</p>
                        <p className="text-xs font-semibold text-gray-900 dark:text-gray-200">
                          {formatCurrency(customer.contractAmount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">현재 잔액</p>
                        <p className="text-xs font-semibold text-gray-900 dark:text-gray-200">
                          {formatCurrency(customer.currentBalance)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">월 사용량</p>
                        <p className="text-xs font-semibold text-gray-900 dark:text-gray-200">
                          {formatCurrency(customer.monthlyUsage)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )
        })}
      </div>

      {/* 고객 등록/수정 모달 */}
      <CustomerFormModal
        mode={modalMode}
        open={modalOpen}
        initial={modalInitial}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />

      {/* 삭제 확인 다이얼로그 */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-[210] flex items-center justify-center p-4 bg-black/50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && deletingProfile === null) setConfirmDelete(null)
          }}
        >
          <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden">
            <div className="p-6">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-50">고객 비활성화</h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                <span className="font-semibold text-gray-900 dark:text-gray-200">{confirmDelete.name}</span>{' '}
                (profile: <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">{confirmDelete.profile}</code>) 엔트리를
                활성 고객 목록에서 제외합니다. 기존 비용·지원 이력은 감사 목적으로 보존됩니다.
              </p>
            </div>
            <div className="px-6 pb-5 flex items-center gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={deletingProfile !== null}
                className="flex-1 py-3 rounded-2xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition disabled:opacity-50"
              >
                취소
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deletingProfile !== null}
                className="flex-1 py-3 rounded-2xl bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold shadow-lg shadow-red-600/20 hover:from-red-500 hover:to-red-600 transition disabled:opacity-60"
              >
                {deletingProfile ? '처리 중…' : '비활성화'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
